import React, {ChangeEvent} from "react";
import * as _ from "lodash";
import { toJS } from "mobx";
import { inject, observer } from "mobx-react";

import { UserStore, CommonStore } from "../../stores";
import { User } from "Stores/userStore";
import { Utils } from "../../common/utils";

import { SelectWrapper } from "Components/Common/SelectWrapper";
import {Button, Col, Row, Modal, FormGroup, FormLabel, FormControl, Form, Spinner} from "react-bootstrap";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";
import DatePicker from "react-datepicker";
import { MapType, Mapping, Committee, Course, Student } from "Stores/commonStore";

interface KeyAdminCheckProps {
    isAdmin: boolean
    isChecked: boolean
    isKeyAdminNote: string | null
    onChange: (next: boolean) => void
}
function KeyAdminCheck({isAdmin, isKeyAdminNote, isChecked, onChange}: KeyAdminCheckProps) {
    return (
        <>
            <SimpleCheckbox disabled={!Utils.isSuperAdmin()} isChecked={isChecked} id={"user-form-key-admin"} label="Key Admin" onChange={onChange} />
            <span>{isKeyAdminNote}</span>
        </>
    );
}

interface UserFormModalState {
    showModal: boolean
    isCreatingOrUpdating: boolean
    firstName: string
    lastName: string
    suffix: string
    prefix: string
    email: string
    isActive: boolean
    groups: string[]
    isAdmin: boolean
    isKeyAdmin: boolean
    isKeyAdminNote: string | null
    isTemporary: boolean
    removeOn: Date | undefined
    user: User | undefined
    committees: Committee[] | undefined
    courses: Course[] | undefined
    students: Student[] | undefined
    hasAdded: boolean
    hasSentInvite: boolean
    hasCopiedInvite: boolean
}

interface UserFormModalProps {
    userStore?: UserStore
    commonStore?: CommonStore
    currentUser?: User
    domainWhitelist?: string | null
    handleSubmitUser: () => void
    handleCloseModal: () => void
}

const reactSelectModalStyle = {menuPortal: base => ({...base, zIndex: 99999})};

@inject("userStore", "commonStore")
@observer
export class UserFormModal extends React.Component<UserFormModalProps, UserFormModalState> {

    statusToEnabledBoolean = (status: string) => {
        // If status is active or pending, toggle status is active
        // Else status should be inactive, toggle status is inactive
        return (status.toLowerCase() !== "inactive");
    };

    state = {
        suffix: this.props.currentUser && this.props.currentUser.suffix ? this.props.currentUser.suffix : "",
        prefix: this.props.currentUser && this.props.currentUser.prefix ? this.props.currentUser.prefix : "",
        firstName: this.props.currentUser && this.props.currentUser.first_name ? this.props.currentUser.first_name : "",
        lastName: this.props.currentUser && this.props.currentUser.last_name ? this.props.currentUser.last_name : "",
        email: this.props.currentUser && this.props.currentUser.email ? this.props.currentUser.email : "",
        // setting status default to active
        isActive: this.props.currentUser ? this.statusToEnabledBoolean(this.props.currentUser.status) : true,
        groups: this.props.currentUser && this.props.currentUser.groups ? this.props.currentUser.groups.slice() : [],
        isAdmin: this.props.currentUser && this.props.currentUser.is_admin ? this.props.currentUser.is_admin : false,
        isKeyAdmin: this.props.currentUser && this.props.currentUser.is_key_admin ? this.props.currentUser.is_key_admin : false,
        isKeyAdminNote: null,
        isTemporary: !!(this.props.currentUser && this.props.currentUser.remove_on),
        removeOn: this.props.currentUser && this.props.currentUser.remove_on ? new Date(this.props.currentUser.remove_on) : undefined,
        isCreatingOrUpdating: false,
        showModal: true,
        user: undefined,
        committees: [],
        courses: [],
        students: [],
        hasAdded: false,
        hasSentInvite: false,
        hasCopiedInvite: false,
    };

    baseState;
    isUpdating = false;

    isValid = () => {
        const { firstName, lastName, email} = this.state;
        return firstName && lastName && email;
    };

    resetState = () => {
        this.setState({...this.baseState, hasAdded: true});
    };

    showHasSentInvite = () => {
        this.setState({hasSentInvite: true}, () => {
            setTimeout(() => {
                this.setState({hasSentInvite: false});
            }, 10000);
        });
    };

    saveUser = (addAnother: boolean) => {
        this.isUpdating = true;
        if (!this.props.userStore) return;
        // TODO: use the school that has been chosen by user
        //  hunter 2020-04-14: API will need to be updated to allow this. Currently the API uses the
        //   school_id in the api route being called.
        const creator = Utils.getCurrentUser();
        if ( !creator ) {
            return;
        }

        let removeOn: any = null;
        if(this.state.removeOn) {
            const date = new Date(this.state.removeOn);
            removeOn = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 1, 0, 0, 0).toISOString();
        }

        const user = {
            id: this.props.currentUser ? this.props.currentUser.ruuid : undefined,
            suffix: this.state.suffix,
            prefix: this.state.prefix,
            first_name: this.state.firstName,
            last_name: this.state.lastName,
            email: this.state.email,
            groups: this.state.groups,
            status: this.state.isActive ? "active" : "inactive",
            created_by: creator.email,
            is_admin: this.state.isAdmin,
            remove_on: removeOn,
        };
        if (this.state.isAdmin) {
            user["is_key_admin"] = this.state.isKeyAdmin;
        }

        this.props.currentUser ?
            this.props.userStore.updateUser(user).then((user) => {
                this.saveMappings(user, addAnother);
            }).catch(() => {
                this.handleSaveError();
            }) :
            this.props.userStore.createUser(user).then((user) => {
                this.saveMappings(user, addAnother);
            }).catch(() => {
                this.handleSaveError();
            });
    };

    handleSaveError = () => {
        this.isUpdating = false;
        this.forceUpdate();
    };

    saveMappings = (user: User, addAnother: boolean) => {
        Promise.all([
            this.saveMapping(user, "courses"),
            this.saveMapping(user, "students"),
            this.saveMapping(user, "committees", true)
        ]).then(() => {
            this.isUpdating = false;
            if (!addAnother) {
                this.props.handleSubmitUser();
            } else {
                this.resetState();
            }
        }).catch(() => {
            this.handleSaveError();
        });
    };

    async saveMapping(user: User, mapping: Mapping, shouldRetry = false) {
        if (!this.props.userStore) return;

        let newMapping: any = [];
        if (this.state[mapping]) {
            newMapping = this.state[mapping].map((obj: MapType) => {
                if (mapping === "students") {
                    return obj.ruuid;
                }
                return obj.id;
            });
        }

        let added: any;
        let removed: any = [];
        if (this.props.currentUser) {
            const current: MapType[] = this.state.user![mapping];
            const currentMapping = current.map((obj: MapType) => {
                if (mapping === "students") {
                    return obj.ruuid;
                }
                return obj.id;
            });

            added = _.differenceWith(newMapping, currentMapping, (valOne: string | number, valTwo: string | number) => valOne === valTwo);
            removed = _.differenceWith(currentMapping, newMapping, (valOne: string | number, valTwo: string | number) => valOne === valTwo);
        } else {
            added = newMapping;
        }

        if (added.length === 0 && removed.length === 0) return;

        await this.props.userStore.addRemoveFacultyMappings(user, mapping, added, removed, shouldRetry);
    }

    handleStatusChange = (isActive: boolean) => {
        this.setState({isActive});
    };

    handleCommitteesChange = (next: any) => {
        this.setState({committees: next});
    };

    handleCoursesChange = (next: any) => {
        this.setState({courses: next});
    };

    handleStudentsChange = (next: any) => {
        this.setState({students: next});
    };

    handleIsAdminChange = (isAdmin: boolean) => {
        const updatedGroups = this.state.groups.slice();
        this.setState({isAdmin: isAdmin});
        if (isAdmin) {
            this.setState({groups: [...updatedGroups, "administrators"]});
        } else {
            const idx = updatedGroups.indexOf("administrators");
            updatedGroups.splice(idx, 1);
            this.setState({groups: updatedGroups, isKeyAdmin: false});
        }
    };

    handleIsKeyAdminChange = (isKeyAdmin: boolean) => {
        if (isKeyAdmin && this.props.userStore) {
            this.props.userStore.fetchKeyAdminUser().then((user) => {
                if (user) {
                    this.setState({isKeyAdminNote: `
                    There is already a Key Admin assigned for this school. Saving this user will replace ${user.name} (${user.email}) as Key Admin.`});
                }
            });
        }
        this.setState({isKeyAdmin});
    };

    handleIsTemporaryChange = (isTemporary: boolean) => {
        this.setState({isTemporary});
    };

    handleRemoveOnChange = (date) => {
        this.setState({removeOn: date});
    };

    handleCancel = () => {
        if (this.state.hasAdded) {
            this.props.handleSubmitUser();
        } else {
            this.props.handleCloseModal();
        }
    };

    handleModalDismiss = () => {
        this.props.handleCloseModal();
    };

    handleResendInvite = (ev) => {
        ev.stopPropagation();
        this.props.userStore!.resendInviteToUser(this.props.currentUser!);
        this.showHasSentInvite();

        return false;
    };

    handleCopyInvite = async (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>): Promise<void> => {
        ev.stopPropagation();
        const { currentUser, userStore } = this.props;
        if (!currentUser) return;

        try {
            if (userStore) {
                const response = await userStore.client.copyUserInviteLink(currentUser.ruuid);
                const inviteLink = response.result.invite_link;
                if (!inviteLink) {
                    throw new Error("Invite link not found in the response");
                }

                // Copy the invite link to the clipboard
                await navigator.clipboard.writeText(inviteLink);

                // Update the state to show success feedback
                this.setState({ hasCopiedInvite: true }, () => {
                    setTimeout(() => {
                        this.setState({ hasCopiedInvite: false });
                    }, 3000); // Reset after 3 seconds
                });
            }
        } catch (err: unknown) {
            console.error("Failed to copy invite link", err);
        }
    };

    isEmailDomainValid = () => {
        const parts = this.state.email.split("@");
        const domain = parts[1];
        if (!domain) return true;

        const whitelist = this.props.domainWhitelist;
        return !whitelist || whitelist.includes(domain);
    };

    getId = (obj: any) => obj.id;
    getStudentId = (obj: any) => obj.ruuid;

    getLabel = (obj: any) => obj.name;

    renderResendSection = () => {
        if(this.state.hasSentInvite){
            return (<span className="success-text left-margin">Sent</span>);
        }
        return (
            <a className="link left-margin" onClick={this.handleResendInvite}>resend invite</a>
        );
    };

    renderCopyInviteSection = () => {
        if (!Utils.isSuperAdmin()) {
            return null;
        }
        if (this.state.hasCopiedInvite) {
            return <span className="success-text left-margin ms-2">Copied</span>;
        }
        return (
            <a className="link left-margin ms-2" onClick={this.handleCopyInvite}>
                Copy Invite Link
            </a>
        );
    };

    renderStatusSection = () => {
        const isPending = this.props.currentUser?.status.toLowerCase() === "pending";
        return (
            <div className="form-section status">
                <div className="title-form-section">Status</div>
                <div>
                    <SimpleCheckbox
                        id={`user-form-status-${this.props.currentUser ? this.props.currentUser.ruuid : ""}`}
                        className="inline-form-field"
                        onChange={this.handleStatusChange}
                        isChecked={this.state.isActive}
                        label="Active"
                    />
                    {isPending ? (
                        <>
                            {this.renderResendSection()}
                            {this.renderCopyInviteSection()}
                        </>
                    ) : null}
                </div>
            </div>
        );
    };

    renderForm () {
        const { currentUser, userStore, commonStore } = this.props;
        if ( !userStore || !commonStore ) return null;
        const isAdmin = this.state.isAdmin;
        const isKeyAdmin = this.state.isKeyAdmin;

        return (
            <div className=" form-section-group">
                <div className="row-padding-top">
                    <div className="form-section">
                        <div className="title-form-section">General</div>
                        {currentUser ?
                            <FormGroup controlId="user-form-email">
                                <FormLabel>Email:</FormLabel>
                                <FormControl
                                    type="input"
                                    value={this.state.email}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({email: e.target.value})}
                                />
                            </FormGroup>
                        : null}
                            <FormGroup controlId="user-form-prefix">
                                <FormLabel>Prefix:</FormLabel>
                                <FormControl
                                    type="input"
                                    value={this.state.prefix}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({prefix: e.target.value})}
                                />
                            </FormGroup>
                            <FormGroup controlId="user-form-name-first">
                                <FormLabel>First Name:</FormLabel>
                                <FormControl
                                    required
                                    type="input"
                                    value={this.state.firstName}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({firstName: e.target.value})}
                                />
                                <span />
                            </FormGroup>
                            <FormGroup controlId="user-form-name-last">
                                <FormLabel>Last Name:</FormLabel>
                                <FormControl
                                    required
                                    type="input"
                                    value={this.state.lastName}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({lastName: e.target.value})}
                                />
                                <span />
                            </FormGroup>
                            <FormGroup controlId="user-form-suffix">
                                <FormLabel>Suffix:</FormLabel>
                                <FormControl
                                    type="input"
                                    value={this.state.suffix}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({suffix: e.target.value})}
                                />
                            </FormGroup>
                    </div>
                </div>
                <div className="row-padding-top">
                    {!currentUser
                        ?
                        <div className="form-section">
                            <FormGroup controlId="user-form-email">
                                <FormLabel>Email:</FormLabel>
                                <FormControl
                                    required
                                    type="email"
                                    value={this.state.email}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({email: e.target.value})}
                                    isInvalid={ !this.isEmailDomainValid() }
                                />
                                <span />
                                <Form.Control.Feedback type="invalid" >Warning: Email is not in the whitelist</Form.Control.Feedback>
                            </FormGroup>
                        </div>
                        : null}
                </div>
                <div className="row-padding-top">
                    {this.renderStatusSection()}
                    <div className="form-section">
                        <div className="title-form-section">User Expiry (Optional)</div>
                        <div>
                            <DatePicker
                                id="user-expiry-date"
                                placeholderText="Select a date..."
                                selected={this.state.removeOn}
                                onChange={this.handleRemoveOnChange}
                                popperClassName="user-expiry-datepicker-popper"
                                calendarClassName="user-expiry-datepicker-calendar"
                            />
                        </div>
                    </div>
                    <div className="form-section permissions">
                            <div className="title-form-section">Permissions</div>
                            <Col id="user-form-admin-col">
                                {!userStore.userOptions || !userStore.userOptions.groups ?
                                    null :
                                    <React.Fragment>
                                        <div className="permissions-checkbox">
                                            <SimpleCheckbox
                                                isChecked={isAdmin}
                                                id={`user-form-admin-${this.props.currentUser ? this.props.currentUser.ruuid : ""}`}
                                                label="Administrator"
                                                onChange={this.handleIsAdminChange}
                                            />
                                        </div>
                                    </React.Fragment>
                                }
                            </Col>
                            <Col>
                                {!userStore.userOptions || !userStore.userOptions.groups ?
                                    null :
                                    <React.Fragment>
                                        <div className="permissions-checkbox">
                                            <KeyAdminCheck
                                                isChecked={isKeyAdmin}
                                                isAdmin={isAdmin}
                                                isKeyAdminNote={this.state.isKeyAdminNote}
                                                onChange={this.handleIsKeyAdminChange}/>
                                        </div>
                                    </React.Fragment>
                                }
                            </Col>
                    </div>
                </div>
                <div className="row-padding-top">
                    <div className="form-section">
                        <div className="title-form-section">Mentees</div>
                        <SelectWrapper
                            id="user-form-students"
                            hideSelectedOptions={ true }
                            onChange={ this.handleStudentsChange }
                            isClearable={ true }
                            isMulti={true}
                            options={commonStore.students ? commonStore.students : []}
                            value={ this.state.students }
                            getOptionLabel={ Utils.studentToStudentLabel }
                            getOptionValue={ this.getStudentId }
                            styles={reactSelectModalStyle}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            className="react-select-container"
                        />
                    </div>
                    <div className="form-section">
                        <div className="title-form-section">Courses</div>
                        <SelectWrapper
                            id="user-form-courses"
                            hideSelectedOptions={ true }
                            onChange={ this.handleCoursesChange }
                            isClearable={true}
                            isMulti={true}
                            options={commonStore.courses ? commonStore.courses : []}
                            placeholder="Courses"
                            value={this.state.courses}
                            getOptionLabel={this.getLabel}
                            getOptionValue={this.getId}
                            styles={reactSelectModalStyle}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            className="react-select-container"
                        />
                    </div>
                    <div className="form-section">
                        <div className="title-form-section">Committees</div>
                        <SelectWrapper
                            id="user-form-committees"
                            hideSelectedOptions={ true }
                            onChange={ this.handleCommitteesChange }
                            isClearable={true}
                            isMulti={true}
                            options={commonStore.committees ? commonStore.committees : []}
                            placeholder="Committees"
                            value={this.state.committees}
                            getOptionLabel={this.getLabel}
                            getOptionValue={this.getId}
                            styles={reactSelectModalStyle}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            className="react-select-container"
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderFormFooter () {
        const { userStore, currentUser } = this.props;
        if ( !userStore ) return null;
        const isSaving = userStore.isUpdatingUserById !== undefined || this.isUpdating;
        const saveButtonContent = isSaving ? <Spinner animation={"border"} variant={"secondary"} size={"sm"}/> : "Save Changes";
        const saveAndAddButtonContent = isSaving ? <Spinner animation={"border"} variant={"secondary"} size={"sm"} /> : "Save and Create Another";
        return (
            <Row className={currentUser && "row-padding-top"}>
                <div className="save-buttons">
                    <Button
                        id="user-form-cancel"
                        disabled={isSaving}
                        onClick={this.handleCancel}
                        className="cancel-button"
                    >
                        Cancel
                    </Button>
                    {!currentUser
                        ?
                        <Button
                            variant="secondary"
                            id="user-form-save"
                            disabled={!this.isValid() || isSaving}
                            onClick={() => this.saveUser(true)}>
                            {saveAndAddButtonContent}
                        </Button>
                        : null
                    }
                    <Button
                        variant="primary"
                        id="user-form-save"
                        disabled={!this.isValid() || isSaving}
                        onClick={() => this.saveUser(false)}>
                        {saveButtonContent}
                    </Button>
                </div>
            </Row>
        );
    }


    componentDidMount() {
        this.baseState = this.state;
        const {currentUser} = this.props;
        if (!currentUser){
            Utils.pushViewName("CreateUser");
        } else {
            Utils.pushViewName("EditUser");
        }


        if(this.props.userStore && this.props.commonStore) {
            this.props.userStore.fetchUserOptions();
            this.props.commonStore.fetchCommittees();
            this.props.commonStore.fetchCourses("basic");
            this.props.commonStore.fetchStudents();

            if (currentUser) {
                this.props.userStore.fetchUserById(currentUser.ruuid).then((user) => {
                    const committees = toJS(user.committees).map((col: any) => Object.assign({}, col, {id: col.id, name: col.committee_name}));
                    const courses = toJS(user.courses).map((col: any) => Object.assign({}, col, {id: col.id}));
                    const students = toJS(user.students).map((col: any) => Object.assign({}, col, {ruuid: col.ruuid}));
                    this.setState({user: user, committees: committees, courses: courses, students: students});
                });
            }
        }
    }

    componentWillUnmount() {
        Utils.popViewName();
    }

    render() {
        const { userStore, currentUser } = this.props;

        if (!userStore) {
            return null;
        } else {
            return ( !currentUser ?
                <Modal id="user-modal-form" centered scrollable show={true} onHide={this.handleModalDismiss} dialogClassName="modal-container wide">
                    <Modal.Header>
                        <Row>
                            <Col md={12} className="text-lg-left">
                                <span className="modal-title">Create User</span>
                            </Col>
                        </Row>
                    </Modal.Header>
                    <Modal.Body>
                        { this.renderForm() }
                    </Modal.Body>
                    <Modal.Footer>
                        { this.renderFormFooter() }
                    </Modal.Footer>
                </Modal>
                :
                <div id="user-modal-form" className="observation-task-form edit">
                    <div className="header"><span className="title-form">Edit User</span></div>
                    { this.renderForm() }
                    { this.renderFormFooter() }
                </div>
            );
        }
    }
}

